import * as Yup from "yup";

import { appConstants } from "../../config/app-constants";
import {
  AcquisitionTypes,
  FileForStatus,
  MFAssetsTypes,
  MaritalStatus,
  MaximumLimits,
  StockAssetsTypes
} from "../../config/filingConstants";
import {
  IAddIncomeForm,
  IAgriculturalIncomes,
  IAnyOtherAssets,
  IBondsDebentureForm,
  IBusinessIncomes,
  ICDMedical80D,
  ICapitalGains,
  IDeemedCapitalGains,
  IDividendIncome,
  IExemptIncomes,
  IFinancialParticulars,
  IForeignSharesForm,
  IFormITR1,
  IImmovableProperties,
  IInterestIncomes,
  IMutualFundForm,
  INonSalaryTDS,
  IOtherIncomes,
  IProfessionalIncomes,
  IReviewITR1,
  ISaleLandAndBuildingForm,
  ISection80GDonation,
  ISection80GGADonation,
  ISelfTaxPayments,
  IStockOptionsRSUsForm,
  IStocksExtractForm,
  IStocksForm,
  ITaxesCollectedAtSources,
  IVirtualDigitalAssets
} from "../../config/interfaces";
import { UtilService } from "../../services";

const AddressZipCountrySchema = {
  PinCode: Yup.string()
    .trim()
    .matches(appConstants.PINCODE_REGEX, "Invalid Pincode/Zipcode")
    .required("Pincode/Zipcode is required"),
  ZipCode: Yup.string()
    .trim()
    .max(8, "Pincode/Zipcode must be 8 characters")
    .required("Pincode/Zipcode is required"),
  CountryCode: Yup.string().trim().required("Country is required"),
  StateCode: Yup.string().trim().required("State is required"),
  CityOrTownOrDistrict: Yup.string()
    .trim()
    .min(1, "Please enter at least 1 characters")
    .max(50, "City must be less than 50 characters")
    .required("City is required")
    .matches(appConstants.CITY_NAME_REGEX, "invalid city name")
};

// used in add/edit income offcanvas form and also in below schema
export const IncomeDetailsSalaryFormSchema = {
  EmployerName: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(125, "Employer Name must be less than 75 characters")
    .required("Employer Name is required"),
  EmployerCategory: Yup.string().required("Employer Category is required"),
  EmployerTAN: Yup.string()
    .trim()
    .matches(appConstants.TAN_REGEX, "Invalid TAN of the Employer")
    .required("TAN of the Employer is required"),
  TotalTDSSal: Yup.string().trim().required("Total TDS is required"),
  EmployerAddress: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(75, "Employer Address must be less than 75 characters")
    .required("Employer Address is required"),
  ...AddressZipCountrySchema,
  SalaryAsPerSection17_1: Yup.string().required("This is required")
};

// used in add/edit income offcanvas form
export const IncomeDetailsSalaryFormInitValues: IAddIncomeForm = {
  "EmployerName": "",
  "EmployerCategory": "",
  "EmployerTAN": "",
  "TotalTDSSal": "",
  "EmployerAddress": "",
  "PinCode": "",
  "ZipCode": "",
  "CountryCode": "91",
  "StateCode": "09",
  "CityOrTownOrDistrict": "",
  "GrossSalary": "", // sum of I(a) + I(b) + I(c) + I(d) + I(e) + I(f)
  // I(a)
  "SalaryAsPerSection17_1": "",
  "SalaryOtherAllowances": [
    ["Basic Salary", ""],
    ["HRA", ""],
    ["LTA", ""]
  ],
  // I(b)
  "PerquisitesUnderSection17_2": "",
  "PerquisitesDescriptions": [["", ""]],
  // I(c)
  "ProfitsInLieuOfSalaryUnderSection17_3": "",
  "ProfitsInLieuDescriptions": [["", ""]],
  // I(d)
  "IncomeUnderSection89ANotifiedCountry": "",
  "IncomeUnderSection89ADescriptions": [["", ""]],
  // I(e)
  "IncomeUnderSection89AOtherThanNotifiedCountry": "",
  // I(f)
  "IncomeTaxableDuringThePreviousYear89A": "",
  // II. Allowances Exempt
  "ExemptAllowancesUnderSection10": "",
  "ExemptAllowancesDescriptions": [
    ["HRA", ""],
    ["LTA", ""]
  ],
  // III. Net Salary (I - II)
  "NetSalary": "", // sum of (GrossSalary - ExemptAllowancesUnderSection10)
  // IV. Deductions
  "SalaryTotalDeductions": "", // sum of 16ia, 16ii, 16iii
  "StandardDeductions16ia": "50000",
  "EntertainmentAllowance16ii": "",
  "ProfessionalTaxUnderSection16iii": "",
  // V. Relief under section 89
  "ReliefUnderSection89": "",
  // VI. Income claimed for relief under section 89A
  "IncomeClaimedForReliefUnderSection89A": "",
  // VII. Income chargeable under head salaries (III-IV-V-VI)
  "SalaryIncomeChargeable": ""
};

// used in add/edit "Any Other Income Sources" offcanvas for "Add" Interest incomes
export const InterestIncomesFormInitValues: IInterestIncomes = {
  SavingBankIncomes: [["", ""]],
  DepositIncomes: [["", ""]],
  P2PInvestments: [["", ""]],
  BondInvestments: [["", ""]],
  PFIncomes: [["", ""]],
  IncomeTaxRefundInterestAmt: [["", ""]],
  OtherInterestIncomes: [["", ""]]
};

// used in add/edit "Any Other Income Sources" offcanvas for "Add" Agricultural Income
export const AgriculturalIncomeFormInitValues: IAgriculturalIncomes = {
  GrossAgricultureReceipt: "",
  ExpenditureOnAgriculture: "",
  UnabsorbedAgricultureLoss: "",
  AgricultureLandDetails: [
    {
      DistrictName: "",
      Pincode: "",
      Measurement: "",
      OwnershipStatus: "",
      WaterSource: ""
    }
  ],
  AgrNetIncome: 0
};

// used in add/edit "Any Other Income Sources" offcanvas for "Add" Exempt incomes
export const ExemptIncomesFormInitValues: IExemptIncomes = {
  InterestPPFIncomes: [["", ""]],
  InterestNREIncomes: [["", ""]],
  OtherExemptIncomes: [["", "", ""]]
};

// used in add/edit "Any Other Income Sources" offcanvas for "Add" Any other income
export const DividendIncomesFormInitValues: IDividendIncome = {
  DomesticCompanyIncomes: [["", "", ""]],
  MutualFundsIncomes: [["", "", ""]]
};

// used in add/edit "Any Other Income Sources" offcanvas for "Add" Any other income
export const OtherIncomesFormInitValues: IOtherIncomes = {
  // AnyOnlineGaming: [{ Source: "", Description: "", Amount: "" }],
  AnyOnlineGaming: [["", "", ""]],
  // AnyInvoiceDiscounting: [{ Name of Platform: "", Amount: "" }],
  AnyInvoiceDiscounting: [["", ""]],
  // AnyOtherIncomes: [{ Source: "", Description: "", Amount: "" }],
  AnyOtherIncomes: [["", "", ""]],
  // DepreciationSection57Incomes: [{ Source: "", Amount: "" }],
  DepreciationSection57Incomes: [["", ""]],
  // UnderSection89AIncomes: [{ Country: "", TimePeriod: "", Amount: "" }],
  UnderSection89AIncomes: [["", "", ""]],
  TaxationRelief89AAmount: "",
  IncomeNonNotifiedCountryAmount: "",
  IncomeTaxablePreviousYear89A: ""
};

// used in add/edit "Virtual Digital Assets" offcanvas for "Add" form
export const VirtualDigitalAssetsFormInitValues: IVirtualDigitalAssets = {
  NFTAssetName: "",
  PurchaseDate: "",
  SaleDate: "",
  TotalPurchasePrice: "",
  TotalSalePrice: "",
  Gains: ""
};

// used in add/edit "Capital Gains" offcanvas for "Add" form
export const CapitalGainsFormInitValues: ICapitalGains = {
  CapitalGainsType: "",
  NFTAssetName: "",
  PurchaseDate: "",
  SaleDate: "",
  TotalPurchasePrice: "",
  TotalSalePrice: "",
  Gains: ""
};

export const ForeignSharesFormInitValues: IForeignSharesForm = {
  TypeAssets: "Listed",
  SaleDate: "",
  PurchaseDate: "",
  DescriptionAssetSold: "",
  TotalSalePrice: "",
  TotalPurchasePrice: "",
  TransferExpense: "",
  Gains: ""
};

export const BondsAndDebentureFormInitValues: IBondsDebentureForm = {
  TypeBonds: "Listed Debenture",
  SaleDate: "",
  PurchaseDate: "",
  DescriptionAssetSold: "",
  TotalSalePrice: "",
  TotalPurchasePrice: "",
  TransferExpense: "",
  Gains: ""
};

// Stock listed initial Values
export const StockFormListedInitValues: IStocksForm = {
  TypeAssets: StockAssetsTypes.Listed_Long_10_Per,
  Acquisition: AcquisitionTypes.On_or_Before_31_01_2018,
  STTPaid: "Yes",
  ISINCode: "",
  NameOfShare: "",
  SalePrice: "",
  TransferExpense: "",
  TotalSaleConsideration: "",
  TotalCostAcquisition: "",
  TotalFairMarketValue: "",
  CapitalGains: ""
};

// Stock listed initial Extracted Values
export const StockExtractedFormValues: IStocksExtractForm = {
  long_term: {
    ...StockFormListedInitValues,
    TypeAssets: StockAssetsTypes.Listed_Long_10_Per
  },
  short_term: {
    ...StockFormListedInitValues,
    TypeAssets: StockAssetsTypes.Listed_Short_15_Per
  }
};

// StockOptionsRSUsForm listed initial Values
export const StockOptionsRSUsInitValues: IStockOptionsRSUsForm = {
  TypeOfStock: "Indian ESOP&RSU - Listed",
  SaleDate: "",
  ExerciseDate: "",
  DescriptionOfSale: "",
  TotalSalePrice: "",
  TransferPrice: "",
  ExerciseValue: "",
  PaySTT: "",
  FairMarketValue: "",
  DescriptionAssetSold: "",
  Gains: ""
};

//  SaleLandAndBuilding listed initial Values
export const SaleLandAndBuildingInitValues: ISaleLandAndBuildingForm = {
  SaleDate: "",
  PurchaseDate: "",
  DescriptionAssetSold: "",
  TotalSalePrice: "",
  TotalPurchasePrice: "",
  TransferExpense: "",
  CapitalGains: "",
  CostImprovementDetails: "",
  PropertyAddress: "",
  PinCode: "",
  AddressLine: "",
  CountryCode: "",
  StateCode: "",
  CityOrTownOrDistrict: "",
  BuyerDetails: [
    {
      BuyerName: "",
      Aadhaar: "",
      Amount: "",
      Percentage: "",
      Pan: ""
    }
  ],
  StampDuty: "",
  SaleConsideration: "",
  Balance: "",
  Deduction: "",
  SplRateTax: 0,
  NrlRateIncome: 0
};

//  MutualFund listed initial Values
export const MutualFundFormInitValues: IMutualFundForm = {
  TypeAssets: MFAssetsTypes.Listed_Long_10_Per,
  STTPaid: "",
  DescriptionAssetSold: "",
  NetSalePrice: "",
  TransferExpense: "",
  TotalSalePrice: "",
  TotalFairMarketValue: "",
  TotalPurchasePrice: "",
  CapitalGains: ""
};

// propertiesOnRentDetails form schema
export const PropertiesOnRentFormSchema = {
  Address: Yup.object().shape({
    ResidenceNo: Yup.string().trim().required("Flat/Door/Block No. is required"),
    ResidenceName: Yup.string().trim().required("Building/House Name is required"),
    RoadOrStreet: Yup.string().trim().required("Road/Street is required"),
    LocalityOrArea: Yup.string().trim().required("Area/Locality is required"),
    ...AddressZipCountrySchema
  }),
  InterestPreConstruction: Yup.string().trim().required("This field is required"),
  InterestPostConstruction: Yup.string().trim().required("Interest on Housing Loan is required"),
  OwnerDetails: Yup.array().of(
    Yup.object().shape({
      FullName: Yup.string().required("Name is required"),
      Pan: Yup.string()
        .trim()
        .required("PAN Number is required")
        .matches(appConstants.PAN_REGEX, "Invalid PAN Number"),
      Percentage: Yup.string().trim().required("Amount is required")
    })
  ),
  TenantDetails: Yup.array().of(
    Yup.object().shape({
      TenantName: Yup.string().required("Tenant Name is required"),
      // Aadhaar: Yup.string()
      // 	.trim()
      // 	.matches(appConstants.AADHAAR_REGEX, "Invalid Aadhaar Number")
      // 	.required("Aadhaar Number is required"),
      Pan: Yup.string()
        .trim()
        .required("PAN Number is required")
        .matches(appConstants.PAN_REGEX, "Invalid PAN Number")
    })
  )
};

// selfOccupiedProperties form schema
export const SelfOccupiedPropertiesFormSchema = {
  Address: Yup.object().shape({
    ResidenceNo: Yup.string().trim().required("Flat/Door/Block No. is required"),
    ResidenceName: Yup.string().trim().required("Building/House Name is required"),
    RoadOrStreet: Yup.string().trim().required("Road/Street is required"),
    LocalityOrArea: Yup.string().trim().required("Area/Locality is required"),
    ...AddressZipCountrySchema
  }),
  InterestPreConstruction: Yup.string().trim().required("This field is required"),
  InterestPostConstruction: Yup.string().trim().required("Interest on Housing Loan is required"),
  OwnerDetails: Yup.array().of(
    Yup.object().shape({
      FullName: Yup.string().required("Name is required"),
      Pan: Yup.string()
        .trim()
        .required("PAN Number is required")
        .matches(appConstants.PAN_REGEX, "Invalid PAN Number"),
      Percentage: Yup.string().trim().required("Amount is required")
    })
  )
};

// MutualFund Form listed Security Schema
export const MutualFundFormSchema = {
  TypeAssets: Yup.string().trim().required("Type of Assets is required"),
  STTPaid: Yup.string().required("STT Paid is required"),
  DescriptionAssetSold: Yup.string().trim().required("Description Of Asset Sold is required"),
  NetSalePrice: Yup.string().required("Net Sale Price is required"),
  TransferExpense: Yup.string().required("Transfer Expenses is required"),
  TotalFairMarketValue: Yup.string().required("Total Fair Market Value is required"),
  TotalPurchasePrice: Yup.string().required("Total Purchase Price is required")
};

export const DeemedCapitalGainsInitValue: IDeemedCapitalGains = {
  loTermCapitalGains: false,
  loUnutilizedCapitalGains: "",
  loDeemedTotalAmount: "",
  loOtherDeemedAmount: "",
  shTermCapitalGains: false,
  shUnutilizedCapitalGains: "",
  shDeemedTotalAmount: "",
  shOtherDeemedAmount: ""
};

// used in add/edit Deemed capital gains offcanvas for "Add" form
export const DeemedCapitalGainsFormSchema = Yup.object().shape(
  {
    loTermCapitalGains: Yup.string().trim(),
    loUnutilizedCapitalGains: Yup.string().when("loTermCapitalGains", ([_status], schema) => {
      return _status === "true"
        ? schema.trim().required("This field is required")
        : schema.trim().notRequired();
    }),
    loOtherDeemedAmount: Yup.string().when("loTermCapitalGains", ([_status], schema) => {
      return _status === "true"
        ? schema.trim().required("This field is required")
        : schema.trim().notRequired();
    }),

    loDeemedTotalAmount: Yup.string().trim(),
    shTermCapitalGains: Yup.string().trim(),
    shUnutilizedCapitalGains: Yup.string().when("shTermCapitalGains", ([_status], schema) => {
      return _status === "true"
        ? schema.trim().required("This field is required")
        : schema.trim().notRequired();
    }),
    shOtherDeemedAmount: Yup.string().when("shTermCapitalGains", ([_status], schema) => {
      return _status === "true"
        ? schema.trim().required("This field is required")
        : schema.trim().notRequired();
    }),
    shDeemedTotalAmount: Yup.string().trim()
  },
  [
    ["loTermCapitalGains", "loUnutilizedCapitalGains"],
    ["loTermCapitalGains", "loOtherDeemedAmount"],
    ["shTermCapitalGains", "shUnutilizedCapitalGains"],
    ["shTermCapitalGains", "shOtherDeemedAmount"]
  ]
);

// SaleLandAndBuilding Form listed Security Schema
export const SaleLandAndBuildingFormSchema = {
  SaleDate: Yup.string().trim().required("Sale Date is required"),
  PurchaseDate: Yup.string().trim().required("Purchase Date is required"),
  TotalSalePrice: Yup.string().required("Total Sale Price is required"),
  TotalPurchasePrice: Yup.string().required("Total Purchase Price is required"),
  DescriptionAssetSold: Yup.string().required("Description Of Asset Sold is required"),
  AddressLine: Yup.string().trim().required("Address Line is required"),
  ...AddressZipCountrySchema,

  BuyerDetails: Yup.array().of(
    Yup.object().shape({
      BuyerName: Yup.string().required("Buyer Name is required"),
      Aadhaar: Yup.string()
        .trim()
        .matches(appConstants.AADHAAR_REGEX, "Invalid Aadhaar Number")
        .required("Aadhaar Number is required"),
      Pan: Yup.string()
        .trim()
        .required("PAN Number is required")
        .matches(appConstants.PAN_REGEX, "Invalid PAN Number"),
      Amount: Yup.string().trim().required("Amount is required"),
      Percentage: Yup.string().trim().required("Ownership Percentage is required")
    })
  )
};

// Stocks Form listed Security Schema
export const StocksFormSchema = {
  TypeAssets: Yup.string().trim().required("Type of Assets is required"),
  SalePrice: Yup.string().required("Full value of consideration is required"),
  TransferExpense: Yup.string().required("Transfer Expenses is required"),
  TotalFairMarketValue: Yup.string().required("Total Fair Market Value is required"),
  TotalCostAcquisition: Yup.string().required("Total cost of Acquisition is required")
};

// StockOptionsRSUs Form listed Security Schema
export const StockOptionsRSUsFormSchema = {
  TypeOfStock: Yup.string().trim().required("Type Of Stock is required"),
  SaleDate: Yup.string().trim().required("Sale Date is required"),
  ExerciseDate: Yup.string().trim().required("Exercise Date is required"),
  DescriptionOfSale: Yup.string().trim().notRequired(),
  TotalSalePrice: Yup.string().trim().required("Total Sale Price is required"),
  TransferPrice: Yup.string().trim().notRequired(),
  ExerciseValue: Yup.string().trim().required("Exercise Value is required"),
  PaySTT: Yup.string().trim().notRequired(),
  FairMarketValue: Yup.string().trim().notRequired(),
  DescriptionAssetSold: Yup.string().trim().notRequired(),
  Gains: Yup.string().trim().notRequired(),
  BuyerDetails: Yup.array().of(Yup.array().of(Yup.string().required("This field is required")))
};

// used in add/edit "Virtual Digital Assets" offcanvas for "Add" form
export const VirtualDigitalAssetsFormSchema = {
  // NFTAssetName: Yup.string()
  //   .trim()
  //   .min(3, "Please enter at least 3 characters")
  //   .required("NFT Asset Name is required"),
  PurchaseDate: Yup.string().trim().required("Purchase Date is required"),
  SaleDate: Yup.string().trim().required("Sale Date is required"),
  TotalPurchasePrice: Yup.string().trim().required("Total Purchase Price is required"),
  TotalSalePrice: Yup.string().trim().required("Total Sale Price is required")
};

// used in add/edit "Any Other Assets" offcanvas for "Add" form
export const OtherAssetsFormInitValues: IAnyOtherAssets = {
  Description: "",
  PurchaseDate: "",
  SaleDate: "",
  TotalPurchasePrice: "",
  TotalSalePrice: "",
  TransferExpense: "",
  AddCostOfImprovementExpanses: false,
  ImprovementExpenses: [["", ""]]
};

// used in add/edit "Any Other Assets" offcanvas for "Add" form
export const OtherAssetsFormSchema = {
  Description: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(50, "Description must be less than 50 characters")
    .required("Description is required"),
  PurchaseDate: Yup.string().trim().required("Purchase Date is required"),
  SaleDate: Yup.string().trim().required("Sale Date is required"),
  TotalPurchasePrice: Yup.string().trim().required("Total Purchase Price is required"),
  TotalSalePrice: Yup.string().trim().required("Total Sale Price is required")
};

// used in add/edit "professional incomes" offcanvas for "Add" form
export const ProfessionalIncomeFormInitValues: IProfessionalIncomes = {
  BusinessName: "",
  Description: "",
  BusinessCode: "",
  InCash: "",
  OtherMode: "",
  ElectronicMode: "",
  GrossReceipt: "",
  TotalSection44ADA: ""
};

// used in add/edit "professional incomes" offcanvas for "Add" form
export const ProfessionalIncomeFormSchema = {
  BusinessName: Yup.string().trim().required("Business Name is required"),
  // Description: Yup.string().trim(),
  BusinessCode: Yup.string().trim().required("Business Code is required"),
  OtherMode: Yup.string().trim().required("Any other mode other than a and b"),
  ElectronicMode: Yup.string().trim().required("This field is required"),
  InCash: Yup.string().trim().required("Receipts in cash is required")
};

// used in add/edit "business incomes" offcanvas for "Add" form
export const BusinessIncomeFormInitValues: IBusinessIncomes = {
  BusinessName: "",
  Description: "",
  BusinessCode: "",
  InCash: "",
  OtherMode: "",
  ElectronicMode: "",
  GrossTurnover: "",
  Total: "",
  SixPerElectronicMode: "",
  EightPerIncashOthermode: ""
};

// used in add/edit "business incomes" offcanvas for "Add" form
export const BusinessIncomeFormInitValuesFormSchema = {
  BusinessName: Yup.string().trim().required("Business Name is required"),
  // Description: Yup.string().trim(),
  BusinessCode: Yup.string().trim().required("Business Code is required"),
  OtherMode: Yup.string().trim().required("Any other mode is required"),
  ElectronicMode: Yup.string().trim().required("This field is required"),
  InCash: Yup.string().trim().required("In Cash is required")
};

// used in add/edit financial particulars offcanvas for "Add" form
export const FinancialParticularFormInitValues: IFinancialParticulars = {
  UnsettledPayables: "",
  OutstandingSecuredLoans: "",
  OutstandingUnsecuresLoans: "",
  Advances: "",
  CapitalInvestments: "",
  OtherLiabilities: "",
  TotalLiabilities: "",
  UncollectedReceivables: "",
  TotalInventoryValue: "",
  CashInHand: "",
  FixedAssets: "",
  ClosingBalance: "",
  LoansAndAdvances: "",
  OtherAssets: "",
  TotalAssets: ""
};

// used in add/edit financial particulars offcanvas for "Add" form
export const FinancialParticularsFormSchema = {
  UnsettledPayables: Yup.string().trim().required("Unsettled Payables is required"),
  // OutstandingSecuredLoans: Yup.string().trim().required("Outstanding Secured Loans is required"),
  // OutstandingUnsecuresLoans: Yup.string()
  //   .trim()
  //   .required("Outstanding Unsecures Loans is required"),
  // Advances: Yup.string().trim().required("Advances is required"),
  // CapitalInvestments: Yup.string().trim().required("Capital Investments is required"),
  // OtherLiabilities: Yup.string().trim().required("Other Liabilities is required"),
  // TotalLiabilities: Yup.string().trim(),
  UncollectedReceivables: Yup.string().trim().required("Uncollected Receivables is required"),
  TotalInventoryValue: Yup.string().trim().required("Total Inventory Value is required"),
  CashInHand: Yup.string().trim().required("Cash In Hand is required")
  // FixedAssets: Yup.string().trim().required("Fixed Assets is required"),
  // ClosingBalance: Yup.string().required("Closing Balance with Banks is required"),
  // LoansAndAdvances: Yup.string().trim().required("Loans And Advances is required"),
  // OtherAssets: Yup.string().trim().required("Other Assets is required"),
  // TotalAssets: Yup.string().trim()
};

export const ForeignSharesFormSchema = {
  TypeAssets: Yup.string().required("Type of Assets is required"),
  SaleDate: Yup.string().trim().required("Sale Date is required"),
  PurchaseDate: Yup.string().trim().required("Purchase Date is required"),
  DescriptionAssetSold: Yup.string()
    .trim()
    .min(3, "Please enter at least 3 characters")
    .max(50, "Description must be less than 50 characters")
    .required("Description is required"),
  TotalPurchasePrice: Yup.string().trim().required("Total Purchase Price is required"),
  TotalSalePrice: Yup.string().trim().required("Total Sale Price is required")
  // TransferExpense: Yup.string().trim().required("Transfer Expense is required")
};

export const BondsAndDebentureFormSchema = {
  TypeBonds: Yup.string().required("Type of Bonds is required"),
  SaleDate: Yup.string().trim().required("Sale Date is required"),
  PurchaseDate: Yup.string().trim().required("Purchase Date is required"),
  // DescriptionAssetSold: Yup.string()
  //   .trim()
  //   .min(3, "Please enter at least 3 characters")
  //   .max(50, "Description must be less than 50 characters")
  //   .required("Description is required"),
  TotalPurchasePrice: Yup.string().trim().required("Total Purchase Price is required"),
  TotalSalePrice: Yup.string().trim().required("Total Sale Price is required")
  // TransferExpense: Yup.string().trim().required("Transfer Expense is required")
};

// used in add/edit "Donations to charitable organizations - Section 80G" offcanvas for "Add" form
export const Section80GDonationFormSchema = {
  FullAddress: Yup.string().trim().required("Full Address is required"),
  ...AddressZipCountrySchema,
  DeductionLimit: Yup.string().trim().required("Limit on Deduction is required"),
  QualifyingPercentage: Yup.string().trim().required("Qualifying Percentage is required"),
  CashDonation: Yup.string().trim().required("Donation Amount is required"),
  NonCashDonation: Yup.string().trim().required("Donation Amount is required")
};

// used in add/edit "Donations to charitable organizations - Section 80G" offcanvas for "Add" form
export const Section80GDonationFormInitValues: ISection80GDonation = {
  OrganizationName: "",
  DoneePAN: "",
  DoneeName: "",
  FullAddress: "",
  CityOrTownOrDistrict: "",
  ZipCode: "",
  PinCode: "",
  CountryCode: "91",
  StateCode: "09",
  DeductionLimit: "NO_LIMIT",
  QualifyingPercentage: "100",
  CashDonation: "",
  NonCashDonation: "",
  TotalDonation: 0
};

// used in add/edit "Donations to charitable organizations - Section 80GGA" offcanvas for "Add" form
export const Section80GGADonationFormSchema = {
  FullAddress: Yup.string().trim().required("Full Address is required"),
  ...AddressZipCountrySchema,
  CashDonation: Yup.string().trim().required("Donation Amount is required"),
  NonCashDonation: Yup.string().trim().required("Donation Amount is required")
};

// used for 80D medical insurance and health checkup

export const Medical80DInitValue: ICDMedical80D = {
  SelfAndFamilySeniorCitizen: false,
  ParentSeniorCitizen: false,
  ForSelfAndFamily: false,
  ForParents: false,
  SelfAndFamilyMedicalInsurance: "",
  SelfAndFamilyHealthCheckup: "",
  SelfAndFamilyMedicalExpenditure: "",
  ParentsMedicalInsurance: "",
  ParentsHealthCheckup: "",
  ParentsMedicalExpenditure: "",
  TotalSection80D: "",
  TotalHealthCheckup: "",
  TotalExpenditure: ""
};

export const Medical80DFormSchema = Yup.object().shape(
  {
    ForParents: Yup.boolean(),
    ForSelfAndFamily: Yup.boolean(),
    SelfAndFamilySeniorCitizen: Yup.boolean(),
    ParentSeniorCitizen: Yup.boolean(),
    SelfAndFamilyMedicalInsurance: Yup.string().when("ForSelfAndFamily", {
      is: true,
      then: (schema) =>
        schema
          .trim()
          .required("Medical Insurance Premium is required")
          .test("max-limit", "Medical Insurance Premium cannot exceed the limit", function (value) {
            const { SelfAndFamilySeniorCitizen } = this.parent;
            const numValue = Number(value);
            if (isNaN(numValue)) {
              return false;
            }
            const maxLimit = SelfAndFamilySeniorCitizen
              ? MaximumLimits.Section80DSenior
              : MaximumLimits.Section80DNonSenior;
            return (
              numValue <= Number(maxLimit) ||
              this.createError({
                message: `Insurance Deduction limit for ${
                  SelfAndFamilySeniorCitizen ? "senior" : "non-senior"
                } citizens - ${maxLimit}`
              })
            );
          }),
      otherwise: (schema) => schema.trim().notRequired()
    }),
    ParentsMedicalInsurance: Yup.string().when("ForParents", {
      is: true,
      then: (schema) =>
        schema
          .trim()
          .required("Medical Insurance Premium is required")
          .test("max-limit", "Medical Insurance Premium cannot exceed the limit", function (value) {
            const { ParentSeniorCitizen } = this.parent;
            const numValue = Number(value);
            if (isNaN(numValue)) {
              return false;
            }
            const maxLimit = ParentSeniorCitizen
              ? MaximumLimits.Section80DSenior
              : MaximumLimits.Section80DNonSenior;
            return (
              numValue <= Number(maxLimit) ||
              this.createError({
                message: `Insurance Deduction limit for ${
                  ParentSeniorCitizen ? "senior" : "non-senior"
                } citizens - ${maxLimit}`
              })
            );
          }),
      otherwise: (schema) => schema.trim().notRequired()
    })
  },
  [
    ["ForSelfAndFamily", "SelfAndFamilyMedicalInsurance"],
    ["ForParents", "ParentsMedicalInsurance"]
  ]
);

// used in add/edit "Donations to charitable organizations - Section 80GGA" offcanvas for "Add" form
export const Section80GGADonationFormInitValues: ISection80GGADonation = {
  DonationCause: "",
  DoneePAN: "",
  DoneeName: "",
  FullAddress: "",
  CityOrTownOrDistrict: "",
  ZipCode: "",
  PinCode: "",
  CountryCode: "91",
  StateCode: "09",
  CashDonation: "",
  NonCashDonation: "",
  TotalDonation: 0
};

// used in add/edit "TDS Non salary" offcanvas for "Add" form
export const NonSalaryTDSFormSchema = Yup.object().shape(
  {
    DeductorTAN: Yup.string().trim().required("TAN of Deductor is required"),
    DeductorName: Yup.string().trim().required("Name of Deductor is required"),
    TotalTaxDeducted: Yup.string().trim().notRequired(),
    ClaimOrTransferTDSCredits: Yup.boolean(),
    TDSCreditRelatingTo: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("TDS credit relating to is required")
        : schema.trim().notRequired();
    }),
    TDSCreditAmount: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("TDS Credit is required")
        : schema.trim().notRequired();
    }),
    PANOfOtherPerson: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("PAN of Other Person is required")
        : schema.trim().notRequired();
    }),
    IncomeRelatingToTDSCredit: Yup.string().when(
      "ClaimOrTransferTDSCredits",
      ([_status], schema) => {
        return _status
          ? schema.trim().required("Income relating to TDS Credit is required")
          : schema.trim().notRequired();
      }
    ),
    TaxDeductedClaimedForThisYear: Yup.string().trim().required("Tax Deducted claimed is required"),
    IncomeAgainstTDS: Yup.string().trim().required("Income against is required"),
    TypeOfIncome: Yup.string().trim().required("Type of Income is required"),
    FinancialYearTDSDeducted: Yup.string().trim().required("Financial Year is required")
  },
  [
    ["ClaimOrTransferTDSCredits", "TDSCreditRelatingTo"],
    ["ClaimOrTransferTDSCredits", "TDSCreditAmount"],
    ["ClaimOrTransferTDSCredits", "PANOfOtherPerson"],
    ["ClaimOrTransferTDSCredits", "IncomeRelatingToTDSCredit"]
  ]
);

// used in add/edit "TDS Non salary" offcanvas for "Add" form
export const NonSalaryTDSFormInitValues: INonSalaryTDS = {
  DeductorTAN: "",
  DeductorName: "",
  TotalTaxDeducted: "",
  ClaimOrTransferTDSCredits: false,
  TDSCreditRelatingTo: "",
  TDSCreditAmount: "",
  PANOfOtherPerson: "",
  IncomeRelatingToTDSCredit: "",
  TaxDeductedClaimedForThisYear: "",
  IncomeAgainstTDS: "",
  TypeOfIncome: "",
  FinancialYearTDSDeducted: ""
};

// used in add/edit "TDS on Sale/Rent of Immovable Property" offcanvas for "Add" form
export const ImmovablePropertiesFormSchema = Yup.object().shape(
  {
    BuyerPAN: Yup.string().trim().required("TAN of Deductor is required"),
    BuyerName: Yup.string().trim().required("Name of Deductor is required"),
    TotalTaxDeducted: Yup.string().trim().notRequired(),
    ClaimOrTransferTDSCredits: Yup.boolean(),
    TDSCreditRelatingTo: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("TDS credit relating to is required")
        : schema.trim().notRequired();
    }),
    TDSCreditAmount: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("TDS Credit is required")
        : schema.trim().notRequired();
    }),
    PANOfOtherPerson: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("PAN of Other Person is required")
        : schema.trim().notRequired();
    }),
    IncomeRelatingToTDSCredit: Yup.string().when(
      "ClaimOrTransferTDSCredits",
      ([_status], schema) => {
        return _status
          ? schema.trim().required("Income relating to TDS Credit is required")
          : schema.trim().notRequired();
      }
    ),
    TaxDeductedClaimedForThisYear: Yup.string().trim().required("Tax Deducted claimed is required"),
    IncomeAgainstTDS: Yup.string().trim().required("Income against is required"),
    TypeOfIncome: Yup.string().trim().required("Type of Income is required"),
    FinancialYearTDSDeducted: Yup.string().trim().required("Financial Year is required")
  },
  [
    ["ClaimOrTransferTDSCredits", "TDSCreditRelatingTo"],
    ["ClaimOrTransferTDSCredits", "TDSCreditAmount"],
    ["ClaimOrTransferTDSCredits", "PANOfOtherPerson"],
    ["ClaimOrTransferTDSCredits", "IncomeRelatingToTDSCredit"]
  ]
);

// used in add/edit "TDS on Sale/Rent of Immovable Property" offcanvas for "Add" form
export const ImmovablePropertiesFormInitValues: IImmovableProperties = {
  BuyerPAN: "",
  BuyerName: "",
  TotalTaxDeducted: "",
  ClaimOrTransferTDSCredits: false,
  TDSCreditRelatingTo: "",
  TDSCreditAmount: "",
  PANOfOtherPerson: "",
  IncomeRelatingToTDSCredit: "",
  TaxDeductedClaimedForThisYear: "",
  IncomeAgainstTDS: "",
  TypeOfIncome: "",
  FinancialYearTDSDeducted: ""
};

// used in add/edit "TDS on Sale/Rent of Immovable Property" offcanvas for "Add" form
export const TaxesCollectedFormSchema = Yup.object().shape(
  {
    CollectorTAN: Yup.string().trim().required("TAN of Deductor is required"),
    CollectorName: Yup.string().trim().required("Name of Deductor is required"),
    TotalTaxCollected: Yup.string().trim().notRequired(),
    ClaimOrTransferTDSCredits: Yup.boolean(),
    TDSCreditRelatingTo: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("TDS credit relating to is required")
        : schema.trim().notRequired();
    }),
    TDSCreditAmount: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("TDS Credit is required")
        : schema.trim().notRequired();
    }),
    PANOfOtherPerson: Yup.string().when("ClaimOrTransferTDSCredits", ([_status], schema) => {
      return _status
        ? schema.trim().required("PAN of Other Person is required")
        : schema.trim().notRequired();
    }),
    TaxCollectedClaimedForThisYear: Yup.string()
      .trim()
      .required("Tax Deducted claimed is required"),
    ExpenditureAgainstTDS: Yup.string().trim().required("Income against is required"),
    FinancialYearTDSCollected: Yup.string().trim().required("Financial Year is required")
  },
  [
    ["ClaimOrTransferTDSCredits", "TDSCreditRelatingTo"],
    ["ClaimOrTransferTDSCredits", "TDSCreditAmount"],
    ["ClaimOrTransferTDSCredits", "PANOfOtherPerson"],
    ["ClaimOrTransferTDSCredits", "IncomeRelatingToTDSCredit"]
  ]
);

// used in add/edit "TDS on Sale/Rent of Immovable Property" offcanvas for "Add" form
export const TaxesCollectedFormInitValues: ITaxesCollectedAtSources = {
  CollectorTAN: "",
  CollectorName: "",
  TotalTaxCollected: "",
  ClaimOrTransferTDSCredits: false,
  TDSCreditRelatingTo: "",
  TDSCreditAmount: "",
  PANOfOtherPerson: "",
  TaxCollectedClaimedForThisYear: "",
  ExpenditureAgainstTDS: "",
  FinancialYearTDSCollected: ""
};

// used in add/edit "TDS on Sale/Rent of Immovable Property" offcanvas for "Add" form
export const SelfTaxPaymentsFormSchema = Yup.object().shape({
  Amount: Yup.string().trim().required("Amount is required"),
  ChallanNo: Yup.string().trim().required("Challan no is required"),
  DepositDate: Yup.string().trim().required("Date is required"),
  BSRCode: Yup.string().trim().required("Income against is required")
});

// used in add/edit "TDS on Sale/Rent of Immovable Property" offcanvas for "Add" form
export const SelfTaxPaymentsFormInitValues: ISelfTaxPayments = {
  Amount: "",
  ChallanNo: "",
  DepositDate: "",
  BSRCode: ""
};

export const FilingValidationSchema = Yup.object().shape(
  {
    FirstName: Yup.string().when("FirstName", ([_FirstName], schema) => {
      return _FirstName
        ? schema
            .trim()
            .min(3, "Please enter at least 3 characters")
            .max(25, "First Name must be less than 25 characters")
            .matches(appConstants.NAME_REGEX, "Invalid First Name")
        : schema.trim().notRequired();
    }),
    MiddleName: Yup.string().when("MiddleName", ([_MiddleName], schema) => {
      return _MiddleName
        ? schema
            .trim()
            .min(3, "Please enter at least 3 characters")
            .max(25, "Middle Name must be less than 25 characters")
            .matches(appConstants.NAME_REGEX, "Invalid Middle Name")
        : schema.trim().notRequired();
    }),
    SurNameOrOrgName: Yup.string()
      .trim()
      .min(3, "Please enter at least 3 characters")
      .max(75, "Last Name must be less than 75 characters")
      .required("Last Name is required"),
    DOB: Yup.string().required("Date of birth is required"),
    FatherName: Yup.string()
      .trim()
      .min(3, "Please enter at least 3 characters")
      .max(175, "Father's name must be less than 175 characters")
      .matches(appConstants.NAME_REGEX, "Invalid Father's name")
      .required("Father's name is required"),
    Gender: Yup.string()
      .oneOf(["male", "female"], "Please select gender")
      .required("Gender is required"),
    MaritalStatus: Yup.string()
      .oneOf(MaritalStatus, "Please select marital status")
      .required("Marital status is required"),
    Status: Yup.string()
      .oneOf(FileForStatus, "Please select status")
      .required("Status is required"),
    MobileNo: Yup.string()
      .trim()
      .matches(appConstants.MOBILE_NUMBER, "Invalid Mobile Number")
      .required("Mobile Number is required"),
    EmailAddress: Yup.string()
      .trim()
      .max(125, "Email Address must be less than 125 characters")
      .matches(appConstants.EMAIL_REGEX, "Invalid Email Address")
      .required("Email Address is required"),
    CountryCodeMobile: Yup.string().trim().notRequired(),
    MobileNoSec: Yup.string().when("MobileNoSec", ([_MobileNoSec], schema) => {
      return _MobileNoSec
        ? schema.trim().matches(appConstants.MOBILE_NUMBER, "Invalid Secondary Mobile No")
        : schema.trim().notRequired();
    }),
    EmailAddressSec: Yup.string().when("EmailAddressSec", ([_EmailAddressSec], schema) => {
      return _EmailAddressSec
        ? schema
            .trim()
            .max(125, "Secondary Email Address must be less than 125 characters")
            .matches(appConstants.EMAIL_REGEX, "Invalid Secondary Email Address")
        : schema.trim().notRequired();
    }),
    STDCodeLandlineNumber: Yup.string().trim().notRequired(),
    LandlineNumber: Yup.string().trim().notRequired(),
    Address: Yup.object().shape({
      ResidenceNo: Yup.string()
        .trim()
        .min(1, "Please enter at least 1 characters")
        .max(50, "Flat/Door No. must be less than 50 characters")
        .required("Flat/Door No. is required"),
      ResidenceName: Yup.string()
        .trim()
        .min(1, "Please enter at least 1 characters")
        .max(50, "Premises Name must be less than 50 characters")
        .required("Premises Name is required"),
      RoadOrStreet: Yup.string()
        .trim()
        .min(1, "Please enter at least 1 characters")
        .max(50, "Road/Street must be less than 50 characters")
        .required("Road/Street is required"),
      LocalityOrArea: Yup.string()
        .trim()
        .min(1, "Please enter at least 1 characters")
        .max(50, "Area/Locality must be less than 50 characters")
        .required("Area/Locality is required"),
      ...AddressZipCountrySchema,
      ResidentialStatus: Yup.string()
        .trim()
        .max(50, "Residential Status must be less than 50 characters")
        .required("Residential Status is required")
    }),
    FilingReturnInfo: Yup.object().shape({
      RegimeType: Yup.string().required("Regime Type is required"),
      Filed: Yup.string().when("FiledToggle", ([FiledToggle], schema) => {
        return FiledToggle
          ? schema.trim().required("Filed is required")
          : schema.trim().notRequired();
      }),

      FiledNotice: Yup.string().when("FiledNoticeToggle", ([FiledNoticeToggle], schema) => {
        return FiledNoticeToggle
          ? schema.trim().required("Filed Notice is required")
          : schema.trim().notRequired();
      })
    }),
    PAN: Yup.string()
      .trim()
      .matches(appConstants.PAN_REGEX, "Invalid PAN Number")
      .required("PAN Number is required"),
    AadhaarCardNo: Yup.string().when("AadhaarEnrolmentId", ([AadhaarEnId], schema) => {
      return AadhaarEnId
        ? schema.trim().notRequired()
        : schema
            .trim()
            .matches(appConstants.AADHAAR_REGEX, "Invalid Aadhaar Number")
            .required("Either Aadhaar Number or Aadhaar Enrolment Number is required");
    }),
    AadhaarEnrolmentId: Yup.string().when("AadhaarCardNo", ([_AadhaarCardNo], schema) => {
      return _AadhaarCardNo
        ? schema.trim().notRequired()
        : schema
            .trim()
            .matches(appConstants.AADHAAR_ENROLMENT_REGEX, "Invalid Aadhaar Enrolment Number")
            .required("");
    }),
    NRIStayInIndia1: Yup.string().when(
      "Address.ResidentialStatus",
      ([ResidentialStatus], schema) => {
        return ResidentialStatus === "Non-Resident"
          ? schema.trim().required("This field is required")
          : schema.trim().notRequired();
      }
    ),

    NRIStayInIndia2: Yup.string().when(
      "Address.ResidentialStatus",
      ([ResidentialStatus], schema) => {
        return ResidentialStatus === "Non-Resident"
          ? schema.trim().required("This field is required")
          : schema.trim().notRequired();
      }
    ),

    NRIJurisdictionDetails: Yup.array().when(
      "Address.ResidentialStatus",
      ([ResidentialStatus], schema) => {
        return ResidentialStatus === "Non-Resident"
          ? Yup.array().of(Yup.array(Yup.string().trim().required("This field is required")))
          : Yup.array().of(Yup.array(Yup.string().trim().notRequired()));
      }
    ),

    AddtnlBankDetails: Yup.array().of(
      Yup.object().shape({
        UseForRefund: Yup.boolean()
          .oneOf([true, false], "Please select Primary bank account")
          .required("Primary bank account is required"),
        IFSCCode: Yup.string().trim().required("IFSC Code is required"),
        BankName: Yup.string().trim().required("Bank Name is required"),
        BankAccountNo: Yup.string().trim().required("Account Number is required"),
        BankAccountType: Yup.string().trim().required("Account Type is required")
      })
    ),
    CommonDeductions: Yup.object().shape({
      TotalSection80C: Yup.string()
        .trim()
        .test(
          `should-be-in-between-0-to-${MaximumLimits.Section80C}`,
          `Amount should not exceed ${UtilService.convertToPrice({
            price: MaximumLimits.Section80C,
            decimalUpto: 0
          })}`,
          (amount) => {
            if (amount) {
              return Number(amount) > Number(MaximumLimits.Section80C) ? false : true;
            }
            return true;
          }
        ),
      TotalSection80CCC: Yup.string()
        .trim()
        .test(
          `should-be-in-between-0-to-${MaximumLimits.Section80CCC}`,
          `Amount should not exceed ${UtilService.convertToPrice({
            price: MaximumLimits.Section80CCC,
            decimalUpto: 0
          })}`,
          (amount) => {
            if (amount) {
              return Number(amount) > Number(MaximumLimits.Section80CCC) ? false : true;
            }
            return true;
          }
        ),
      TotalSection80TTA: Yup.string()
        .trim()
        .test(
          `should-be-in-between-0-to-${MaximumLimits.Section80TTA}`,
          `Amount should not exceed ${UtilService.convertToPrice({
            price: MaximumLimits.Section80TTA,
            decimalUpto: 0
          })}`,
          (amount) => {
            if (amount) {
              return Number(amount) > Number(MaximumLimits.Section80TTA) ? false : true;
            }
            return true;
          }
        )
    }),
    OtherDeductions: Yup.object().shape({
      TotalSection80EE: Yup.string()
        .trim()
        .test(
          `should-be-in-between-0-to-${MaximumLimits.Section80EE}`,
          `Amount should not exceed ${UtilService.convertToPrice({
            price: MaximumLimits.Section80EE,
            decimalUpto: 0
          })}`,
          (amount) => {
            if (amount) {
              return Number(amount) > Number(MaximumLimits.Section80EE) ? false : true;
            }
            return true;
          }
        ),
      TotalSection80EEA: Yup.string()
        .trim()
        .test(
          `should-be-in-between-0-to-${MaximumLimits.Section80EEA}`,
          `Amount should not exceed ${UtilService.convertToPrice({
            price: MaximumLimits.Section80EEA,
            decimalUpto: 0
          })}`,
          (amount) => {
            if (amount) {
              return Number(amount) > Number(MaximumLimits.Section80EEA) ? false : true;
            }
            return true;
          }
        ),
      TotalSection80EEB: Yup.string()
        .trim()
        .test(
          `should-be-in-between-0-to-${MaximumLimits.Section80EEB}`,
          `Amount should not exceed ${UtilService.convertToPrice({
            price: MaximumLimits.Section80EEB,
            decimalUpto: 0
          })}`,
          (amount) => {
            if (amount) {
              return Number(amount) > Number(MaximumLimits.Section80EEB) ? false : true;
            }
            return true;
          }
        )
    })
  },
  [
    ["FirstName", "FirstName"],
    ["MiddleName", "MiddleName"],
    ["MobileNoSec", "MobileNoSec"],
    ["EmailAddressSec", "EmailAddressSec"],
    ["AadhaarCardNo", "AadhaarEnrolmentId"],
    ["AadhaarEnrolmentId", "AadhaarCardNo"],
    ["Filed", "FiledToggle"],
    ["FiledNotice", "FiledNoticeToggle"],
    ["NRIJurisdictionDetails", "Address.ResidentialStatus"],
    ["NRIStayInIndia1", "Address.ResidentialStatus"],
    ["NRIStayInIndia2", "Address.ResidentialStatus"]
  ]
);
// add edit agricultural validation schema
export const AgriculturalIncomeFormValidationSchema = Yup.object().shape({
  GrossAgricultureReceipt: Yup.string().trim().required("Gross Receipt is required"),
  ExpenditureOnAgriculture: Yup.string().trim().required("Expenditure Incurred is required"),
  UnabsorbedAgricultureLoss: Yup.string()
    .trim()
    .required("Unabsorbed Agricultural Loss is required")
});

export const AddressFormInitValues = {
  ResidenceNo: "",
  ResidenceName: "",
  RoadOrStreet: "",
  LocalityOrArea: "",
  CityOrTownOrDistrict: "",
  ZipCode: "",
  PinCode: "",
  CountryCode: "91",
  StateCode: "09",
  ResidentialStatus: "Resident"
};

export const InitDeductUndChapVIA = {
  // Section80CCD1B: 0,
  Section80C: 0,
  Section80CCC: 0,
  Section80CCDEmployeeOrSE: 0,
  Section80CCDEmployer: 0,
  Section80CCE: 0,
  Section80CCF: 0,
  Section80CCG: 0,
  Section80CCH: 0,
  Section80D: 0,
  Section80DD: 0,
  Section80DDB: 0,
  Section80E: 0,
  Section80EE: 0,
  Section80EEA: 0,
  Section80EEB: 0,
  Section80G: 0,
  Section80GG: 0,
  Section80GGA: 0,
  Section80GGB: 0,
  Section80GGC: 0,
  Section80IA: 0,
  Section80IB: 0,
  Section80IC: 0,
  Section80JJA: 0,
  Section80JJAA: 0,
  Section80LA: 0,
  Section80M: 0,
  Section80P: 0,
  Section80QQB: 0,
  Section80RRB: 0,
  Section80TTA: 0,
  Section80TTB: 0,
  Section80U: 0
};

export const FilingFormInitValues: IFormITR1 = {
  FirstName: "",
  MiddleName: "",
  SurNameOrOrgName: "",
  DOB: "",
  DOCommencement: "",
  FatherName: "",
  Gender: "male",
  MaritalStatus: "Prefer not to disclose",
  Status: "Individual",
  AadhaarCardNo: "",
  AadhaarEnrolmentId: "",
  PAN: "",
  CountryCodeMobile: "+91",
  MobileNo: "",
  EmailAddress: "",
  CountryCodeMobileNoSec: "+91",
  MobileNoSec: "",
  EmailAddressSec: "",
  STDCodeLandlineNumber: "",
  LandlineNumber: "",
  Address: { ...AddressFormInitValues },
  FilingReturnInfo: {
    FormName: "ITR-1",
    RegimeType: "NEW_TAX_REGIME",
    Filed: "",
    FiledNotice: "",
    FiledToggle: true,
    FiledNoticeToggle: false
  },
  NRIStayInIndia1: "",
  NRIStayInIndia2: "",
  NRIJurisdictionDetails: [["", ""]],
  AddtnlBankDetails: [
    {
      UseForRefund: "true",
      IFSCCode: "",
      BankName: "",
      BankAccountNo: "",
      BankAccountType: "Savings"
    }
  ],
  IncomeDetails: {
    Salaries: [],
    // Any Other Income Sources
    InterestIncomes: null,
    DividendIncomes: null,
    AgriculturalIncomes: null,
    ExemptIncomes: null,
    OtherIncomes: null,
    VirtualDigitalAssets: [],
    ForeignShares: [],
    Stocks: [],
    MutualFunds: [],
    OtherAssets: [],
    SaleLandAndBuildings: [],
    StockOptionsRSUs: [],
    RISelfOccupiedProperties: [],
    RIGivenOnRentProperties: [],
    ProfessionalIncomes: null,
    BusinessIncomes: null,
    FinancialParticulars: null,
    DeemedCapitalGains: null,
    BondsAndDebenture: []
  },
  CommonDeductions: {
    ParentsAgeGroup: "",
    HaveMedicalInsurance: false,
    ICDMedical80D: null,
    HavePreventiveHealthCheck: false,
    PreventiveHealthSelfAndFamily: "",
    PreventiveHealthParent: "",
    MedicalExpendituresSelfAndFamily: "",
    MedicalExpendituresParent: "",
    TotalSection80C: "",
    TotalSection80CCC: "",
    TotalSection80CCD1Employee: "",
    TotalSection80CCD2Employer: "",
    TotalSection80TTA: ""
  },
  DonationsEduHRADeductions: {
    TotalSection80E: "",
    TotalSection80GG: "",
    S80GGPaidRentMonths: "",
    Section80GDonationDetails: [],
    Section80GGADonationDetails: []
  },
  OtherDeductions: {
    DependentDisabled80DD: { Nature: "", Relation: "" },
    TotalSection80DD: "",
    SelfDisabilitySection80U: "NONE",
    TotalSection80U: "",
    TotalSection80EE: "",
    TotalSection80EEA: "",
    TotalSection80EEB: "",
    TotalSection80QQB: "",
    TotalSection80RRB: "",
    TotalSection80GGC: "",
    TotalSection80JJA: "",
    AgeOfPerson80DDB: "",
    TotalSection80DDB: "",
    TotalSection80CCH: ""
  },
  TaxesPaidTDSandTCS: {
    NonSalaryTDS: [],
    ImmovableProperties: [],
    TaxesCollectedAtSources: [],
    SelfTaxPayments: [],
    CarryForwardLosses: {
      PreviousYearsLosses: [["", "", ""]],
      DepreciationLosses: [["", ""]],
      FilingAndDueDates: [["", "", ""]]
    }
  },
  TotalTDSonSalaries: 0, // TDS on Salary
  TotalTDSonOthThanSals: 0, // NonSalaryTDS
  TotalTDS3Details: 0, // ImmovableProperties
  TotalSchTCS: 0, // TaxesCollectedAtSources
  TotalSelfTaxPayments: 0, // SelfTaxPayments
  TaxPaidBefore15June: 0,
  TaxPaidBefore15Sept: 0,
  TaxPaidBefore15Dec: 0,
  TaxPaidBefore15Mar: 0,
  DeductUndChapVIA: { ...InitDeductUndChapVIA },
  TempDeductUndChapVIA: { ...InitDeductUndChapVIA },
  TotalChapVIADeductions: 0,
  GrossSalary: 0,
  Salary: 0,
  PerquisitesValue: 0,
  ProfitsInSalary: 0,
  NetSalary: 0,
  DeductionUs16: 0,
  DeductionUs16ia: 50000, // standard deduction
  EntertainmentAlw16ii: 0,
  ProfessionalTaxUs16iii: 0,
  TotalReliefUs89: 0,
  TotalIncClaimedForReliefUs89A: 0,
  IncomeFromSal: 0,
  GrossRentReceived: 0,
  TaxPaidlocalAuth: 0,
  AnnualValue: 0,
  StandardDeduction: 0,
  InterestPayable: 0,
  ArrearsUnrealizedRentRcvd: 0,
  TotalIncomeOfHP: 0,
  OthersInc: {
    TotalInterestIncome: 0,
    TotalDividendIncome: 0,
    TotalAgriculturalIncome: 0,
    TotalExemptIncome: 0,
    TotalOtherIncome: 0
  },
  IncomeOthSrc: 0,
  GrossTotIncome: 0,
  TotalIncome: 0,
  BusOrProfIncomes: {
    PISection44ADA: 0,
    BISection44AD: 0,
    TotalLiabilities: 0,
    TotalAssets: 0
  },
  TotalBusOrProfIncome: 0,
  CapitalGains: {
    TotalStocksGain: 0,
    TotalMutualFundsGain: 0,
    TotalForeignSharesGain: 0,
    TotalLandBuildingGain: 0,
    TotalStockOptionsRSUsGain: 0,
    TotalBondsDebentureGain: 0,
    TotalDeemedCapitalGain: 0,
    TotalAnyOtherAssetsGain: 0
  },
  TotalCapitalGains: 0,
  CapitalGainsSplRateTax: {
    StocksSplRateTax: 0,
    MutualFndSplRateTax: 0,
    LandSplRateTax: 0
  },
  CapitalGainsNrlRateIncome: {
    StocksNrlRateIncome: 0,
    MutualFndNrlRateIncome: 0,
    LandNrlRateIncome: 0
  },
  TotalSplRateTax: 0,
  TotalNrlRateIncome: 0,
  HousePropertyIncomes: {
    TotalSelfOccupied: 0,
    TotalOnRent: 0
  },
  TotalHousePropertyIncome: 0, // Should not exceed -2,00,000, in sum of both self and rent
  VirtualDigitalAssets: {
    TotalCryptoIncome: 0
  },
  TotalVirtualDigitalAsset: 0,
  TaxPaid: {
    TaxesPaid: {
      AdvanceTax: 0,
      TDS: 0,
      TCS: 0,
      SelfAssessmentTax: 0,
      TotalTaxesPaid: 0
    },
    BalTaxPayable: 0
  },
  TotalTaxableIncome: 0,
  ITR1_TaxComputation: {
    IntrstPay: {
      IntrstPayUs234A: 0,
      IntrstPayUs234B: 0,
      IntrstPayUs234C: 0,
      LateFilingFee234F: 0
    },
    TotalTaxPayable: 0,
    Rebate87A: 0,
    TaxPayableOnRebate: 0,
    Surcharge: 0,
    EducationCess: 0,
    GrossTaxLiability: 0,
    Section89: 0,
    NetTaxLiability: 0,
    TotalIntrstPay: 0,
    TotTaxPlusIntrstPay: 0
  },
  LastSavedTimeStamp: 0
};

export const TaxSummaryData: IReviewITR1 = {
  // Permanent Details
  FirstName: "",
  MiddleName: "",
  SurNameOrOrgName: "",
  FullName: "",
  DOB: "",
  PAN: "",
  ResidentialStatus: "",
  RegimeType: "",

  // Income Sources
  "GrossTotalIncome": 0,
  "SalarySummaries": [],
  "TotalNetSalary": 0,
  "TotalStdDedUs16i": 0,
  "TotalEntUs16ii": 0,
  "TotalProfUs16iii": 0,
  "TotalReliefUs89": 0,
  "TotalClaimedReliefUs89A": 0,
  "TotalIncomeFromSalaries": 0,
  "TotalHousePropertyIncome": 0,
  "HousePropertyIncomes": {
    "TotalSelfOccupied": 0,
    "TotalOnRent": 0
  },
  "TotalOtherSourcesIncome": 0,
  "OtherSourcesIncomes": {
    "TotalInterestIncome": 0,
    "TotalDividendIncome": 0,
    "TotalAgriculturalIncome": 0,
    "TotalExemptIncome": 0,
    "TotalOtherIncome": 0
  },
  "TotalBusinessOrProfessionIncome": 0,
  "BusinessOrProfessionIncomes": {
    "PISection44ADA": 0,
    "BISection44AD": 0,
    "TotalLiabilities": 0,
    "TotalAssets": 0
  },
  "TotalCapitalGainsIncome": 0,
  "CapitalGains": {
    "TotalStocksGain": 0,
    "TotalMutualFundsGain": 0,
    "TotalForeignSharesGain": 0,
    "TotalLandBuildingGain": 0,
    "TotalStockOptionsRSUsGain": 0,
    "TotalBondsDebentureGain": 0,
    "TotalDeemedCapitalGain": 0,
    "TotalAnyOtherAssetsGain": 0
    // "SpecialRatesIncome": 0,
    // "TaxSpecialIncome": 0,
    // "NormalIncome": 0
  },
  "TotalVDAIncome": 0,
  "VDAIncomes": {
    "TotalCryptoIncome": 0
  },
  "TotalTaxableIncome": 0,
  "AllTaxesPaid": {
    "TotalTDSonSalary": 0,
    "TotalNonSalaryTDS": 0,
    "TotalTDSonSaleOrRent": 0,
    "TotalTaxesAtSource": 0,
    "TotalSelfTaxPayments": 0
  },
  "TotalDeductions": 0,
  "DeductionSummaries": {
    "TotalSection80C": {
      "DeductionSection": "80C",
      "Amount": 0
    },
    "TotalSection80CCC": {
      "DeductionSection": "80CCC",
      "Amount": 0
    },
    "TotalSection80CCD1Employee": {
      "DeductionSection": "80CCD(1) and 80CCD(1B)",
      "Amount": 0
    },
    "TotalSection80CCD2Employer": {
      "DeductionSection": "80CCD(2)",
      "Amount": 0
    },
    "TotalSection80CCE": {
      "DeductionSection": "80CCE",
      "Amount": 0
    },
    "TotalSection80CCF": {
      "DeductionSection": "80CCF",
      "Amount": 0
    },
    "TotalSection80CCG": {
      "DeductionSection": "80CCG",
      "Amount": 0
    },
    "TotalSection80D": {
      "DeductionSection": "80D",
      "Amount": 0
    },
    "TotalSection80DD": {
      "DeductionSection": "80DD",
      "Amount": 0
    },
    "TotalSection80DDB": {
      "DeductionSection": "80DDB",
      "Amount": 0
    },
    "TotalSection80E": {
      "DeductionSection": "80E",
      "Amount": 0
    },
    "TotalSection80EE": {
      "DeductionSection": "80EE",
      "Amount": 0
    },
    "TotalSection80EEA": {
      "DeductionSection": "80EEA",
      "Amount": 0
    },
    "TotalSection80EEB": {
      "DeductionSection": "80EEB",
      "Amount": 0
    },
    "TotalSection80G": {
      "DeductionSection": "80G",
      "Amount": 0
    },
    "TotalSection80GG": {
      "DeductionSection": "80GG",
      "Amount": 0
    },
    "TotalSection80GGA": {
      "DeductionSection": "80GGA",
      "Amount": 0
    },
    "TotalSection80GGB": {
      "DeductionSection": "80GGB",
      "Amount": 0
    },
    "TotalSection80GGC": {
      "DeductionSection": "80GGC",
      "Amount": 0
    },
    "TotalSection80IA": {
      "DeductionSection": "80IA",
      "Amount": 0
    },
    "TotalSection80IB": {
      "DeductionSection": "80IB",
      "Amount": 0
    },
    "TotalSection80IC": {
      "DeductionSection": "80IC",
      "Amount": 0
    },
    "TotalSection80JJA": {
      "DeductionSection": "80JJA",
      "Amount": 0
    },
    "TotalSection80JJAA": {
      "DeductionSection": "80JJAA",
      "Amount": 0
    },
    "TotalSection80LA": {
      "DeductionSection": "80LA",
      "Amount": 0
    },
    "TotalSection80M": {
      "DeductionSection": "80M",
      "Amount": 0
    },
    "TotalSection80P": {
      "DeductionSection": "80P",
      "Amount": 0
    },
    "TotalSection80QQB": {
      "DeductionSection": "80QQB",
      "Amount": 0
    },
    "TotalSection80RRB": {
      "DeductionSection": "80RRB",
      "Amount": 0
    },
    "TotalSection80TTA": {
      "DeductionSection": "80TTA",
      "Amount": 0
    },
    "TotalSection80TTB": {
      "DeductionSection": "80TTB",
      "Amount": 0
    },
    "TotalSection80U": {
      "DeductionSection": "80U",
      "Amount": 0
    }
  },
  "TotalTaxPaidTDS": 0,
  "TaxPayable": 0, // Total Tax: (tax + taxCess)
  "TaxDetails": {
    "Tax": 0, // Income Tax at normal rates
    "TaxCess": 0 // Health and Education Cess
  },
  "InterestPayable": 0,
  "InterestDetails": {
    "Interest234A": 0,
    "Interest234B": 0,
    "Interest234C": 0,
    "Fee234F": 0
  },
  "TotalTaxPayable": 0,
  "TaxDue": 0,
  "TaxRefund": 0,
  "TaxesDueOrRefund": 0,

  "ItrOption": "1",
  "WhichItr": "ITR1",
  "TaxableIncomeFromSalary": "0",
  "DeductionUs16": "0",
  "StdDeduction": "50000",
  "EntertainmentAllowance": "0",
  "TaxOnEmployment": "0",
  "TaxableIncomeFromHouseProperty": "0",
  "ExpenseUs57": "0",
  "TaxableIncomeFromOtherSources": "0",
  "ShortTermSlab": "",
  "ShortTermSpecial": "",
  "LongTermTen": "",
  "LongTermTwenty": "",
  "CapGainTotal": "0",
  "PresumptiveBusinessIncome": "",
  "PresumptiveGoodsCarriageIncome": "",
  "PresumptiveProfessionIncome": "",
  "RegularBusinessAccountMaintained": "",
  "RegularBusinessAccountNotMaintained": "",
  "SpeculativeIncome": "",
  "RemunerationFromPartnershipsFirms": "",
  "TotalTaxableIncomeFromVda": "0",
  "TaxableIncomeFromVdaCg": "",
  "TaxableIncomeFromVdaBi": "",
  "TaxCreditUS87A": "",
  "IsCred87AApplicable": false,
  "TaxAtNormalRates": "0",
  "TaxAt10Percent": "",
  "TaxAt15Percent": "",
  "TaxAt20Percent": "",
  "HealthEducationCess": "0",
  "AgriRelief": "",
  "TotalTax": "0",
  "BasicExemption": "250000",
  "IsTdsPaid": true,
  "TdsPaid": "40000",
  "IsTcsPaid": false,
  "TcsPaid": "",
  "IsSelfAssTaxPaid": false,
  "SelfAssessmentTaxPaid": "",
  "IsAdvanceTaxPaid": false,
  "AdvanceTaxPaid": "",
  "TaxPaid": "40000",
  "TaxPaymentLink":
    "https://eportal.incometax.gov.in/iec/foservices/#/e-pay-tax-prelogin/user-details",
  "IsGlobalValidationSuccess": true,
  "IsNewRegimeBelatedOriginalReturn": false,
  "IsFilingAllowedWhenPartnerIsLiableForAudit": false
};
